// Helpers for reasoning about a project's version-set timeline.
//
// Two distinct timelines matter and they can diverge:
//
//   - issuance_date — the date the user types in when uploading; this is
//     the "version of record" date a designer would put in a title block.
//     Can be back-dated.
//
//   - published_at — system timestamp of when the user actually clicked
//     Publish. Monotonic; can't be edited after the fact.
//
// "Current" is per-sheet: for each sheet_number, the most recent published
// issuance wins. The drawings list defaults to this view (one row per sheet
// number). Time Machine adds a `published_at <= cutoff` eligibility filter
// before the same per-sheet pick — back-dating a new issuance can't
// retroactively change what was visible on a given calendar day, but within
// the eligible window it still matches the user's mental model ("the May 1
// issuance" wins over "the April 15 one").

/**
 * Comparable string for an ISO date that's safe with `>` / `<` /
 * `localeCompare` even when the value comes back as a JS Date or null.
 * The `.slice(0,10)` clamps timestamps to YYYY-MM-DD so a back-dated
 * `published_at` time-of-day doesn't tip a tie that should be settled
 * by the issuance date itself.
 */
function isoDate(v) {
  if (!v) return '';
  if (typeof v === 'string') return v.slice(0, 10);
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return '';
}
function isoTimestamp(v) {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (v instanceof Date) return v.toISOString();
  return '';
}

/**
 * Reduce a sheets list to one row per sheet_number — the latest version
 * by `issuance_date`, ties broken by `published_at`. Sheets carrying
 * `is_published === false` (drafts) are eligible only when the caller
 * already filtered them in (admin/PM seeing their own drafts).
 *
 * The default drawings list runs this every render. Picking a specific
 * version set in the filter dropdown bypasses the dedupe so the user
 * can see every sheet in that exact issuance.
 */
export function dedupeLatestPerSheet(sheets) {
  if (!Array.isArray(sheets)) return [];
  const byNumber = new Map();
  for (const s of sheets) {
    const key = s.sheet_number || `__noNumber__${s.id}`;
    const prev = byNumber.get(key);
    if (!prev) { byNumber.set(key, s); continue; }
    const ad = isoDate(prev.issuance_date);
    const bd = isoDate(s.issuance_date);
    if (bd > ad) { byNumber.set(key, s); continue; }
    if (bd < ad) continue;
    // Tie on issuance_date — prefer the later publish for determinism.
    const ap = isoTimestamp(prev.published_at);
    const bp = isoTimestamp(s.published_at);
    if (bp > ap) byNumber.set(key, s);
  }
  return Array.from(byNumber.values());
}

/**
 * Does any other sheet in `sheets` carry the same sheet_number with a
 * later `issuance_date`? Drives the "you're looking at an older version"
 * banner in PdfViewer — sheet-by-sheet rather than VS-by-VS, so a project
 * with multiple per-discipline VSes on the same date doesn't false-flag
 * sheets in non-winning VSes.
 */
export function hasNewerSheetVersion(sheet, sheets) {
  if (!sheet || !sheet.sheet_number || !Array.isArray(sheets)) return false;
  const myDate = isoDate(sheet.issuance_date);
  const myPub  = isoTimestamp(sheet.published_at);
  return sheets.some((s) => {
    if (s.id === sheet.id) return false;
    if (s.sheet_number !== sheet.sheet_number) return false;
    if (s.deleted_at) return false;
    const sd = isoDate(s.issuance_date);
    if (sd > myDate) return true;
    if (sd < myDate) return false;
    return isoTimestamp(s.published_at) > myPub;
  });
}

/**
 * Pick the current version set's id from a list — most recent issuance
 * among published sets. Returns null if nothing has been published yet
 * (so the caller can fall through to "show all").
 *
 * Tie-breaking: if two published sets share the same issuance_date,
 * the more recently published one wins (deterministic without being
 * arbitrary).
 *
 * Kept for the few legacy callers that still need a single "anchor" VS
 * (e.g. CompareSetupModal preselecting set A on the compare picker).
 * The drawings list itself no longer relies on this — see
 * `dedupeLatestPerSheet` for the per-sheet model.
 */
export function getCurrentVersionSetId(versionSets) {
  if (!Array.isArray(versionSets) || !versionSets.length) return null;
  const published = versionSets.filter((v) => v.is_published);
  if (!published.length) return null;
  const sorted = [...published].sort((a, b) => {
    const ad = a.issuance_date || '';
    const bd = b.issuance_date || '';
    if (ad !== bd) return bd.localeCompare(ad); // newer issuance first
    const ap = a.published_at || '';
    const bp = b.published_at || '';
    return bp.localeCompare(ap);
  });
  return sorted[0].id;
}

